import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { ProjectDetailsApiService } from '../../services/project-details-api.service';
import { ProjectDetailsStateService } from '../../services/project-details-state.service';
import { ProjectDetailsGridConfigModel } from '../../../common/models/project-details-grid-config.model';
import { ConsultantProjectRateRow } from '../../models/project-details.types';

@Component({
  selector: 'app-consultant-rates-tab',
  templateUrl: './consultant-rates-tab.component.html',
  styleUrls: ['./consultant-rates-tab.component.scss']
})
export class ConsultantRatesTabComponent implements OnInit, OnDestroy {
  ratesForm!: FormGroup;

  consultantsList: string[] = [];
  monthsList: string[] = [];
  currencyLabel: string = '';

  rowData: ConsultantProjectRateRow[] = [];
  columnDefs: any[] = [];
  gridOptions: GridOptions = {};
  agGridAPI!: GridApi;
  agGridColumnAPI!: ColumnApi;

  isSaveEnabled = false;
  isDeleteEnabled = false;
  private isEditing = false;
  private subs = new Subscription();

  // Tracks the pre-edit value of an edited cell, keyed the same way the
  // original service's originalRowData map did:
  // `${Consultant}_${Month}_${field}` -> old value.
  // Needed because by save time the grid row's Month is already the NEW
  // value, but the update payload must send the OLD month (origMonth) so
  // the backend can find the row to update.
  private originalRowData: { [key: string]: any } = {};

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private apiService: ProjectDetailsApiService,
    public stateService: ProjectDetailsStateService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.setupGridConfig();
    this.subscribeToState();
    this.loadReferenceData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  buildForm(): void {
    this.ratesForm = this.fb.group({
      consultant: [null, Validators.required],
      month: [null, Validators.required],
      leverage: [0, [Validators.required, Validators.min(0)]],
      hourlyRate: [0, [Validators.required, Validators.min(0)]]
    });

    this.ratesForm.valueChanges.subscribe(() => {
      if (this.ratesForm.dirty) {
        this.stateService.setUnsavedChanges(true);
      }
    });
  }

  setupGridConfig(): void {
    const gridConfig = new ProjectDetailsGridConfigModel();
    this.columnDefs = gridConfig.consultantProjectRatesColumns.map((col: any) => ({
      headerName: col.headerName.split('_').join(' '),
      field: col.field,
      resizable: true,
      sortable: true,
      editable: col.editable,
      cellStyle: col.cellStyle,
      checkboxSelection: col.checkboxSelection,
      headerCheckboxSelection: col.headerCheckboxSelection,
      hide: !!col.hide,
      flex: 1
    }));

    this.gridOptions = {
      rowSelection: 'multiple',
      groupDefaultExpanded: 1,
      groupSelectsChildren: true,
      suppressAggFuncInHeader: true,
      suppressRowClickSelection: true,
      getRowStyle: (params) => {
        if (params.node.isSelected()) {
          return { background: 'lightblue' };
        }
        if (params.node.data?.dirty) {
          return { background: '#ffc107' };
        }
        return undefined;
      }
    };
  }

  subscribeToState(): void {
    this.subs.add(
      this.stateService.currencyLabel$.subscribe(label => {
        this.currencyLabel = label;
      })
    );

    this.subs.add(
      this.stateService.tableData$.subscribe(data => {
        this.rowData = data.consultantProjectRates ? [...data.consultantProjectRates] : [];
        this.isSaveEnabled = false;
        this.isDeleteEnabled = false;
        this.cdr.detectChanges();
      })
    );
  }

  async loadReferenceData(): Promise<void> {
    try {
      const [employees, months] = await Promise.all([
        this.apiService.getEmployees(),
        this.apiService.getPeriodMonths()
      ]);

      this.consultantsList = employees || [];

      this.monthsList = (months || [])
        .map((m: any) =>
          m.Period_Month ??
          m.period_Month ??
          m.periodMonth ??
          m.Month ??
          m.month ??
          m
        )
        .filter((m: any) => m !== null && m !== undefined && m !== '');

      this.cdr.detectChanges();

    } catch (e) {
      console.error('Error loading consultant rates ref data:', e);
    }
  }

  onGridReady(params: any): void {
    this.agGridAPI = params.api;
    this.agGridColumnAPI = params.columnApi;
  }

  onCellValueChanged(event: any): void {
    const cellKey = `${event.data.Consultant}_${event.data.Month}_${event.colDef.field}`;
    this.originalRowData[cellKey] = event.oldValue;

    event.data.isModified = true;
    event.data.dirty = true;
    event.api.applyTransaction({ update: [event.data] });
    event.api.redrawRows({ rowNodes: [event.node] });
    this.isSaveEnabled = true;
    this.stateService.setUnsavedChanges(true);
  }

  onSelectionChanged(event: any): void {
    const selectedNodes = event.api.getSelectedNodes();
    this.isDeleteEnabled = selectedNodes.length > 0;
    event.api.redrawRows({ rowNodes: selectedNodes });
  }

  onCellEditingStarted(event: any): void {
    this.isEditing = true;
  }

  onCellEditingStopped(event: any): void {
    this.isEditing = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isEditing && !this.isClickInsideGrid(event)) {
      this.agGridAPI?.stopEditing();
    }
  }

  private isClickInsideGrid(event: MouseEvent): boolean {
    const gridElement = document.querySelector('.ag-theme-balham');
    return gridElement?.contains(event.target as Node) ?? false;
  }

  // ==========================================
  // Form & Table Actions
  // ==========================================

  async onSubmitRate(): Promise<void> {
    if (this.ratesForm.invalid) {
      this.ratesForm.markAllAsTouched();
      alert('Please fill all required rate fields.');
      return;
    }

    const formVal = this.ratesForm.value;
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();
    const countryCode = this.stateService.getSelectedCountryCode();

    const periodKey = await this.apiService.getPeriodMonthId(formVal.month);
    const currencyCode = await this.apiService.getCurrencyCode(countryCode);

    // Field names/casing here match the original addConsultantProjectRates()
    // request body exactly - the backend expects this specific shape
    // (periodMonth + periodKey together, leveragePercent not "leverage",
    // rate as a 4-decimal string, editedBy not loginName).
    const rate = parseFloat(formVal.hourlyRate).toFixed(4);

    if (parseFloat(rate) < 0) {
      this.stateService.showNotification({
        Type: 'failure',
        Message: 'Rate must be greater than or equal to zero!'
      });
      return;
    }

    const payload = {
      client: clientCode,
      project: projectCode,
      employee: formVal.consultant,
      rate: rate,
      periodMonth: formVal.month,
      periodKey: periodKey,
      leveragePercent: parseInt(formVal.leverage, 10),
      currencyCode: currencyCode,
      editedBy: this.stateService.employeeName
    };

    try {
      const res = await this.apiService.saveConsultantProjectRate(payload).toPromise();
      this.stateService.showNotification(res);
      this.ratesForm.reset({ leverage: 0 });

      // Refresh table
      const data = await this.apiService.getProjectDetailsTables(clientCode, projectCode);
      this.stateService.setTableData(data);
    } catch (err: any) {
      this.stateService.showNotification(err.error || { Type: 'failure', Message: 'Failed to add consultant rate' });
    }
  }

  async onSaveRates(): Promise<void> {
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();
    const modifiedNodes: any[] = [];

    this.agGridAPI.forEachNode(node => {
      if (node.data?.isModified) {
        modifiedNodes.push(node.data);
      }
    });

    if (modifiedNodes.length === 0) return;

    // Field names/casing here match the original updateConsultantProjectRates()
    // request body exactly - this is what was throwing the
    // "'period_Month'" KeyError from the backend: the old payload never
    // sent period_Month, period_Key, or origMonth at all.
    const payloadArray = [];
    for (const row of modifiedNodes) {
      const monthRowKey = `${row.Consultant}_${row.Month}_Month`;
      const rate = parseInt(row.Rate, 10);

      if (rate < 0) {
        this.stateService.showNotification({
          Type: 'failure',
          Message: 'Rate must be greater than or equal to zero!'
        });
        return;
      }

      const periodKey = await this.apiService.getPeriodMonthId(row.Month);

      payloadArray.push({
        Client: clientCode,
        Project: projectCode,
        employee: row.Consultant,
        OrigConsultant: row.Consultant,
        rate: rate,
        period_Month: row.Month,
        period_Key: periodKey,
        origMonth: this.originalRowData[monthRowKey] ?? row.Month,
        Leverage_Percent: parseInt(row.Leverage, 10),
        editedBy: this.stateService.employeeName
      });
    }

    try {
      const res = await this.apiService.updateConsultantProjectRates(payloadArray).toPromise();
      this.stateService.showNotification(res);
      this.isSaveEnabled = false;
      this.originalRowData = {};
      this.stateService.setUnsavedChanges(false);

      const data = await this.apiService.getProjectDetailsTables(clientCode, projectCode);
      this.stateService.setTableData(data);
    } catch (err: any) {
      this.stateService.showNotification(err.error || { Type: 'failure', Message: 'Failed to update rates' });
    }
  }

  async onDeleteRates(): Promise<void> {
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();
    const selectedRows = this.agGridAPI.getSelectedRows();

    if (selectedRows.length === 0) return;

    // Field names here match the original deleteConsultantProjectRates()
    // request body exactly - it needs "rate" and the raw "periodMonth"
    // value (not a resolved periodKey, and no async lookup is needed here).
    const payloadArray = selectedRows.map(row => ({
      client: clientCode,
      project: projectCode,
      consultant: row.Consultant,
      rate: parseInt(row.Rate, 10),
      periodMonth: row.Month
    }));

    try {
      const res = await this.apiService.deleteConsultantProjectRates(payloadArray).toPromise();
      this.stateService.showNotification(res);
      this.isDeleteEnabled = false;

      const data = await this.apiService.getProjectDetailsTables(clientCode, projectCode);
      this.stateService.setTableData(data);
    } catch (err: any) {
      this.stateService.showNotification(err.error || { Type: 'failure', Message: 'Failed to delete rates' });
    }
  }

  onClearForm(): void {
    this.ratesForm.reset({ leverage: 0 });
  }

  onNextTab(): void {
    this.stateService.setSelectedIndex(2);
  }
}